/**
 * Shared constants for the frontend
 */

import type { PostStatus } from '@feedbackhub/types';

/**
 * All valid post statuses
 */
export const POST_STATUSES: PostStatus[] = [
  'open',
  'under_review',
  'planned',
  'in_progress',
  'completed',
  'closed',
];

/**
 * Active statuses (posts still being worked on)
 */
export const ACTIVE_STATUSES: PostStatus[] = ['open', 'under_review', 'planned', 'in_progress'];

/**
 * Resolved statuses (posts that are done)
 */
export const RESOLVED_STATUSES: PostStatus[] = ['completed', 'closed'];

/**
 * Badge variant mapping for status badges
 */
export type StatusBadgeVariant = 'open' | 'review' | 'planned' | 'progress' | 'complete' | 'closed' | 'secondary';

/**
 * Status display configuration
 * Maps status values to their display labels, colors, and badge variants
 */
export const STATUS_CONFIG: Record<
  PostStatus,
  { label: string; color: string; bgColor: string; darkColor: string; variant: StatusBadgeVariant }
> = {
  open: {
    label: 'Open',
    color: 'text-blue-800',
    bgColor: 'bg-blue-100',
    darkColor: 'dark:bg-blue-900/30 dark:text-blue-400',
    variant: 'open',
  },
  under_review: {
    label: 'Under Review',
    color: 'text-yellow-800',
    bgColor: 'bg-yellow-100',
    darkColor: 'dark:bg-yellow-900/30 dark:text-yellow-400',
    variant: 'review',
  },
  planned: {
    label: 'Planned',
    color: 'text-purple-800',
    bgColor: 'bg-purple-100',
    darkColor: 'dark:bg-purple-900/30 dark:text-purple-400',
    variant: 'planned',
  },
  in_progress: {
    label: 'In Progress',
    color: 'text-orange-800',
    bgColor: 'bg-orange-100',
    darkColor: 'dark:bg-orange-900/30 dark:text-orange-400',
    variant: 'progress',
  },
  completed: {
    label: 'Completed',
    color: 'text-green-800',
    bgColor: 'bg-green-100',
    darkColor: 'dark:bg-green-900/30 dark:text-green-400',
    variant: 'complete',
  },
  closed: {
    label: 'Closed',
    color: 'text-gray-800',
    bgColor: 'bg-gray-100',
    darkColor: 'dark:bg-gray-900/30 dark:text-gray-400',
    variant: 'closed',
  },
};

/**
 * Get full status color class including dark mode
 */
export function getStatusFullColorClass(status: PostStatus): string {
  const config = STATUS_CONFIG[status];
  return config ? `${config.bgColor} ${config.color} ${config.darkColor}` : 'bg-gray-100 text-gray-800';
}

/**
 * Status options for select dropdowns
 */
export const STATUS_OPTIONS = POST_STATUSES.map((status) => ({
  value: status,
  label: STATUS_CONFIG[status].label,
}));

/**
 * Status filter items for admin filters
 */
export const STATUS_FILTER_ITEMS = POST_STATUSES.map((status) => ({
  id: status,
  label: STATUS_CONFIG[status].label,
}));

/**
 * Get status label from status value
 */
export function getStatusLabel(status: PostStatus): string {
  return STATUS_CONFIG[status]?.label || status;
}

/**
 * Get status color classes from status value
 */
export function getStatusColorClass(status: PostStatus): string {
  const config = STATUS_CONFIG[status];
  return config ? `${config.bgColor} ${config.color}` : 'bg-gray-100 text-gray-800';
}

/**
 * React Query cache keys factory
 * Centralizes query key definitions for consistency
 */
export const queryKeys = {
  // Auth
  auth: {
    me: ['auth', 'me'] as const,
  },

  // Boards
  boards: {
    all: ['boards'] as const,
    detail: (slug: string) => ['boards', slug] as const,
  },

  // Posts
  posts: {
    all: ['posts'] as const,
    list: (params: Record<string, unknown>) => ['posts', 'list', params] as const,
    detail: (slug: string) => ['posts', slug] as const,
  },

  // Comments
  comments: {
    list: (postId: string) => ['comments', postId] as const,
  },

  // Votes
  votes: {
    voters: (postId: string) => ['votes', postId, 'voters'] as const,
  },

  // Search
  search: {
    results: (params: Record<string, unknown>) => ['search', params] as const,
  },

  // Profile
  profile: {
    me: ['profile'] as const,
    votes: (page?: number) => ['profile', 'votes', page] as const,
    activity: (page?: number) => ['profile', 'activity', page] as const,
  },

  // Admin
  admin: {
    stats: ['admin', 'stats'] as const,
    users: (params?: Record<string, unknown>) => ['admin', 'users', params] as const,
    posts: (params?: Record<string, unknown>) => ['admin', 'posts', params] as const,
  },
} as const;
